// User-facing guide rendered both inline below the ingestion sliders
// and on the standalone /admin/docs/chunk-tuning page. Pulls every
// piece of copy from chunkTuning.TUNING_GUIDE so a content edit there
// flows through both surfaces.
//
// Two render modes:
//   <ChunkTuningGuide />            — compact, fits inside the form panel
//   <ChunkTuningGuide variant="page" /> — wider, used on the standalone docs page

import React from "react";
import { COLORS } from "../../theme/colors";
import { TUNING_GUIDE } from "./chunkTuning";


export default function ChunkTuningGuide({ variant = "inline" }) {
  const isPage = variant === "page";
  const padding = isPage ? "32px 40px" : "16px 14px";
  const headingSize = isPage ? 22 : 14;
  const bodySize = isPage ? 14 : 12;
  const sectionGap = isPage ? 28 : 16;
  const maxWidth = isPage ? 800 : "auto";

  return (
    <div style={{
      padding, fontSize: bodySize, color: COLORS.text,
      lineHeight: 1.6, maxWidth, margin: isPage ? "0 auto" : 0,
    }}>
      {isPage && (
        <>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 12px" }}>
            {TUNING_GUIDE.headline}
          </h1>
          <p style={{ fontSize: 15, color: COLORS.textMuted, marginBottom: sectionGap }}>
            {TUNING_GUIDE.intro}
          </p>
        </>
      )}

      {/* ─── Recommended default ─────────────────────────────────── */}
      <Section heading={TUNING_GUIDE.default_section.heading} size={headingSize}>
        <div style={{
          background: COLORS.accentSoft || "rgba(59,130,246,0.10)",
          border: `1px solid ${COLORS.border}`,
          padding: "10px 14px", borderRadius: 8,
          fontWeight: 600, color: COLORS.accentText, marginBottom: 10,
        }}>
          {TUNING_GUIDE.default_section.summary}
        </div>
        <p style={{ margin: "0 0 8px" }}>{TUNING_GUIDE.default_section.body}</p>
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {TUNING_GUIDE.default_section.points.map(p => (
            <li key={p.title} style={{ marginBottom: 6 }}>
              <strong>{p.title}.</strong> {p.text}
            </li>
          ))}
        </ul>
      </Section>

      {/* ─── When to deviate ─────────────────────────────────────── */}
      <Section heading={TUNING_GUIDE.deviation_section.heading} size={headingSize} mt={sectionGap}>
        <p style={{ margin: "0 0 10px" }}>{TUNING_GUIDE.deviation_section.intro}</p>
        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%", borderCollapse: "collapse",
            fontSize: bodySize, color: COLORS.text,
          }}>
            <thead>
              <tr style={{
                background: COLORS.surface,
                borderBottom: `1px solid ${COLORS.border}`,
                textAlign: "left",
              }}>
                <th style={cellHead}>If your documents are…</th>
                <th style={{ ...cellHead, whiteSpace: "nowrap" }}>Try</th>
                <th style={cellHead}>Why</th>
              </tr>
            </thead>
            <tbody>
              {TUNING_GUIDE.deviation_section.rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={cell}>{r.label}</td>
                  <td style={{ ...cell, whiteSpace: "nowrap", fontWeight: 600, color: COLORS.accentText }}>
                    {r.values}
                  </td>
                  <td style={cell}>{r.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ─── Per-agent callout ───────────────────────────────────── */}
      <Section
        heading={TUNING_GUIDE.per_agent_callout.heading}
        size={headingSize}
        mt={sectionGap}
        callout
      >
        <p style={{ margin: 0 }}>{TUNING_GUIDE.per_agent_callout.body}</p>
      </Section>

      {/* ─── What you don't need to worry about ──────────────────── */}
      <Section heading={TUNING_GUIDE.automatic_section.heading} size={headingSize} mt={sectionGap}>
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {TUNING_GUIDE.automatic_section.items.map(p => (
            <li key={p.title} style={{ marginBottom: 6 }}>
              <strong>{p.title}</strong> {p.text}
            </li>
          ))}
        </ul>
      </Section>

      {/* ─── How to verify ───────────────────────────────────────── */}
      <Section heading={TUNING_GUIDE.verification_section.heading} size={headingSize} mt={sectionGap}>
        <p style={{ margin: "0 0 10px" }}>{TUNING_GUIDE.verification_section.body}</p>
        <a
          href={TUNING_GUIDE.verification_section.cta.path}
          style={{
            color: COLORS.accentText, textDecoration: "underline", fontSize: bodySize,
          }}
        >
          {TUNING_GUIDE.verification_section.cta.label} {"\u2192"}
        </a>
      </Section>

      {/* The inline panel exposes a permalink to the standalone docs
          page so users can share the URL or open it in its own tab. */}
      {!isPage && (
        <div style={{ marginTop: 14, fontSize: 11, color: COLORS.textMuted }}>
          <a
            href="/chunk-tuning"
            target="_blank"
            rel="noreferrer"
            style={{ color: COLORS.accentText, textDecoration: "underline" }}
          >
            Open this guide in a new tab {"\u2192"}
          </a>
        </div>
      )}
    </div>
  );
}


// ─── Layout helpers ────────────────────────────────────────────────

function Section({ heading, size, mt, callout, children }) {
  return (
    <div style={{
      marginTop: mt || 0,
      paddingTop: callout ? 12 : 0,
      paddingBottom: callout ? 12 : 0,
      paddingLeft: callout ? 14 : 0,
      paddingRight: callout ? 14 : 0,
      background: callout ? (COLORS.accentSoft || "rgba(59,130,246,0.08)") : "transparent",
      borderRadius: callout ? 8 : 0,
      border: callout ? `1px solid ${COLORS.border}` : "none",
    }}>
      <h2 style={{
        fontSize: size, fontWeight: 700, color: COLORS.text,
        margin: "0 0 8px",
      }}>
        {heading}
      </h2>
      {children}
    </div>
  );
}

const cellHead = {
  padding: "8px 10px",
  fontSize: "0.95em",
  fontWeight: 700,
  color: COLORS.textMuted,
};
const cell = {
  padding: "8px 10px",
  verticalAlign: "top",
};
